import PropTypes from 'prop-types';
import { Collapse, Form, Select } from 'antd';
import colors from '../../constants/Colors';
import OnTraccrTextInput from '../../common/inputs/OnTraccrTextInput';
import OnTraccrNumberInput from '../../common/inputs/OnTraccrNumberInput';
import { billingTypeDisplayName, getProjectRateFields } from './ProjectBillingRateHelper';

const { Panel } = Collapse;

export default function ProjectBillingRateConfigurePanels({
  selectedRowKeys,
  templatesMap,
  configsByTemplateId,
  updateConfig,
  userOptions,
  positionOptions,
  equipmentTypeOptions,
  equipmentOptions,
  errorTemplateIds,
  collapseActiveKeys,
  onCollapseActiveKeysChange,
}) {
  const errorTemplateIdSet = new Set(errorTemplateIds);

  const labourRateFields = getProjectRateFields({ type: 'labour' });
  const equipmentRateFields = getProjectRateFields({ type: 'equipment' });

  return (
    <div>
      <div style={{ marginBottom: 16, color: colors.GRAY_800 }}>
        Expand each template to set the project rate name, associations, and billing amounts.
      </div>
      <Collapse
        bordered={false}
        activeKey={collapseActiveKeys}
        onChange={onCollapseActiveKeysChange}
      >
        {selectedRowKeys.map((templateId, panelIndex) => {
          const template = templatesMap[templateId];
          const header = template
            ? `${template.name} (${billingTypeDisplayName(template.type)})`
            : templateId;
          const config = configsByTemplateId[templateId];
          const rateType = config?.type;
          const isLastPanel = panelIndex === selectedRowKeys.length - 1;
          const hasError = errorTemplateIdSet.has(templateId);

          return (
            <Panel
              header={header}
              key={templateId}
              className="budget-tracking-border"
              style={{
                marginBottom: isLastPanel ? 0 : 12,
                ...(hasError ? {
                  borderColor: colors.ONTRACCR_RED,
                  boxShadow: `0 0 0 1px ${colors.FADED_ONTRACCR_RED}`,
                } : {}),
              }}
            >
              {config && (
                <Form layout="vertical" component="div">
                  <Form.Item label="Name" required>
                    <OnTraccrTextInput
                      value={config.name}
                      onChange={(event) => updateConfig(templateId, { name: event.target.value })}
                      placeholder="Rate name"
                    />
                  </Form.Item>
                  {rateType === 'labour' && (
                    <>
                      <Form.Item label="Users">
                        <Select
                          mode="multiple"
                          allowClear
                          showSearch
                          optionFilterProp="label"
                          options={userOptions}
                          value={config.userIds}
                          onChange={(value) => updateConfig(templateId, { userIds: value || [] })}
                          placeholder="Select users"
                          style={{ width: '100%' }}
                        />
                      </Form.Item>
                      <Form.Item label="Positions">
                        <Select
                          mode="multiple"
                          allowClear
                          showSearch
                          optionFilterProp="label"
                          options={positionOptions}
                          value={config.positionIds}
                          onChange={(value) => updateConfig(
                            templateId,
                            { positionIds: value || [] },
                          )}
                          placeholder="Select positions"
                          style={{ width: '100%' }}
                        />
                      </Form.Item>
                      {labourRateFields.map(({ label, value: fieldName }) => (
                        <Form.Item key={fieldName} label={label}>
                          <OnTraccrNumberInput
                            value={config[fieldName]}
                            onChange={(numericValue) => updateConfig(
                              templateId,
                              { [fieldName]: numericValue },
                            )}
                            placeholder={`Enter ${label.toLowerCase()}`}
                          />
                        </Form.Item>
                      ))}
                    </>
                  )}
                  {rateType === 'equipment' && (
                    <>
                      <Form.Item label="Equipment types">
                        <Select
                          mode="multiple"
                          allowClear
                          showSearch
                          optionFilterProp="label"
                          options={equipmentTypeOptions}
                          value={config.equipmentTypeIds}
                          onChange={(value) => updateConfig(
                            templateId,
                            { equipmentTypeIds: value || [] },
                          )}
                          placeholder="Select equipment types"
                          style={{ width: '100%' }}
                        />
                      </Form.Item>
                      <Form.Item label="Equipment">
                        <Select
                          mode="multiple"
                          allowClear
                          showSearch
                          optionFilterProp="label"
                          options={equipmentOptions}
                          value={config.equipmentIds}
                          onChange={(value) => updateConfig(
                            templateId,
                            { equipmentIds: value || [] },
                          )}
                          placeholder="Select equipment"
                          style={{ width: '100%' }}
                        />
                      </Form.Item>
                      {equipmentRateFields.map((billingField) => {
                        const { label, value: fieldName } = billingField;
                        return (
                          <Form.Item key={fieldName} label={label}>
                            <OnTraccrNumberInput
                              value={config[fieldName]}
                              onChange={(numericValue) => updateConfig(
                                templateId,
                                { [fieldName]: numericValue },
                              )}
                              placeholder={`Enter ${label.toLowerCase()}`}
                            />
                          </Form.Item>
                        );
                      })}
                    </>
                  )}
                </Form>
              )}
            </Panel>
          );
        })}
      </Collapse>
    </div>
  );
}

const configureRateConfigShape = PropTypes.shape({
  templateId: PropTypes.string.isRequired,
  name: PropTypes.string,
  type: PropTypes.string,
  regularBillingRate: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])]),
  otBillingRate: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])]),
  dtBillingRate: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])]),
  hourlyBillingRate: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])]),
  userIds: PropTypes.arrayOf(PropTypes.string),
  positionIds: PropTypes.arrayOf(PropTypes.string),
  equipmentTypeIds: PropTypes.arrayOf(PropTypes.string),
  equipmentIds: PropTypes.arrayOf(PropTypes.string),
});

ProjectBillingRateConfigurePanels.propTypes = {
  selectedRowKeys: PropTypes.arrayOf(PropTypes.string).isRequired,
  templatesMap: PropTypes.objectOf(PropTypes.object).isRequired,
  configsByTemplateId: PropTypes.objectOf(configureRateConfigShape).isRequired,
  updateConfig: PropTypes.func.isRequired,
  userOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  positionOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  equipmentTypeOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  equipmentOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  errorTemplateIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  collapseActiveKeys: PropTypes.arrayOf(PropTypes.string).isRequired,
  onCollapseActiveKeysChange: PropTypes.func.isRequired,
};

